import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';
import QrScanner from 'qr-scanner';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { setAuthToken } from '../utils/auth';
import '../styles/LoyaltyCard.css';
import PinRedemptionModal from './PinRedemptionModal';
import { IOSPWAHandler } from '../utils/iosPwaHandler';

function LoyaltyCard({ program: propProgram }) {
  const [program, setProgram] = useState(propProgram || null);
  const { programId } = useParams();
  const [lastRedeemedStamp, setLastRedeemedStamp] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const streamRef = useRef(null);

  const [showPinModal, setShowPinModal] = useState(false);
  const [showPinRedeemModal, setShowPinRedeemModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vendorSettings, setVendorSettings] = useState(null);
  const [isLoadingVendorSettings, setIsLoadingVendorSettings] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [isIOS, setIsIOS] = useState(false);
  const [iosPwaHandler, setIosPwaHandler] = useState(null);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);

  const navigate = useNavigate();

  // Set playsinline attribute on video element (important for iOS)
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.setAttribute('playsinline', 'true');
    }
  }, []);

  // Centralized camera cleanup function
  const cleanupCameraResources = useCallback(() => {
    console.log('Cleaning up camera resources...');
    
    // Stop QR scanner
    if (qrScannerRef.current) {
      console.log('Stopping QR scanner');
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    
    // Stop camera stream
    if (streamRef.current) {
      console.log('Stopping camera stream tracks');
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => {
        console.log('Stopping track:', track.kind, track.id);
        track.stop();
      });
      streamRef.current = null;
    }
    
    // Clear video source
    if (videoRef.current && videoRef.current.srcObject) {
      console.log('Clearing video source');
      videoRef.current.srcObject = null;
      videoRef.current.load(); // Force reload to clear the video element
    }
  }, []);

  // Debounced program data fetch
  const fetchProgramData = useCallback(() => {
    const debouncedFetch = debounce(async () => {
      if (!programId) return;
      
      try {
        const response = await axios.get(`/api/customers/programs/${programId}`);
        console.log('Program data:', response.data);
        setProgram(response.data);
      } catch (error) {
        console.error('Error fetching program data:', error);
      }
    }, 300);
    
    debouncedFetch();
    return debouncedFetch.cancel;
  }, [programId]);

  // Debounced vendor settings fetch
  const fetchVendorSettings = useCallback((vendorId) => {
    const debouncedFetch = debounce(async () => {
      if (!vendorId) {
        console.log('No vendor ID provided');
        return;
      }
      
      try {
        setIsLoadingVendorSettings(true);
        console.log('Fetching settings for vendor:', vendorId);
        const response = await axios.get(`/api/vendors/${vendorId}/redemption-settings`);
        console.log('Received vendor settings:', response.data);
        setVendorSettings(response.data);
      } catch (error) {
        console.error('Error fetching vendor settings:', error);
      } finally {
        setIsLoadingVendorSettings(false);
      }
    }, 300);
    
    debouncedFetch();
    return debouncedFetch.cancel;
  }, []);

  // QR scan handler with proper cleanup
  const handleScan = useCallback(async (data) => {
    console.log('QR scan detected:', data ? 'data found' : 'empty scan');
    
    if (!data) return;
    
    try {
      const qrData = JSON.parse(data);
      
      if (qrData.type === 'visit_checkin' && qrData.programId === parseInt(programId)) {
        // Stop showing scanner and prevent further scans
        setIsLoading(true);
        setShowScanner(false);
        
        // Clean up camera resources immediately
        cleanupCameraResources();
        
        // Process the QR code data
        const response = await axios.post('/api/customers/redeem-visit', { programId: qrData.programId });
        
        setMessage({
          text: response.data.message,
          type: 'success'
        });

        // Refresh program data
        fetchProgramData();
      } else {
        setMessage({
          text: 'Invalid QR code',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setMessage({
        text: error.response?.data?.error || 'Error processing QR code',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [programId, fetchProgramData, cleanupCameraResources]);

  // Handle auth token and initial data loading
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
    
    let programFetchCancel;
    
    if (!propProgram && programId) {
      programFetchCancel = fetchProgramData();
    }
  
    return () => {
      if (programFetchCancel) {
        programFetchCancel();
      }
      cleanupCameraResources();
    };
  }, [propProgram, programId, fetchProgramData, cleanupCameraResources]);

  // Fetch vendor settings when program data is available
  useEffect(() => {
    let settingsFetchCancel;
    
    if (program?.vendor_id) {
      console.log('Found vendor ID:', program.vendor_id);
      settingsFetchCancel = fetchVendorSettings(program.vendor_id);
    } else {
      console.log('No vendor ID found in program data:', program);
    }
    
    return () => {
      if (settingsFetchCancel) {
        settingsFetchCancel();
      }
    };
  }, [program, fetchVendorSettings]);

  // Reset lastRedeemedStamp after 1 second
  useEffect(() => {
    if (lastRedeemedStamp !== null) {
      const timer = setTimeout(() => setLastRedeemedStamp(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [lastRedeemedStamp]);

  // Initialize and manage QR scanner
  useEffect(() => {
    // Only initialize scanner when showScanner is true
    if (!showScanner) {
      return;
    }

    const initializeScanner = async () => {
      try {
        console.log('Initializing QR scanner...');
        
        // First clean up any existing resources to prevent conflicts
        cleanupCameraResources();

        // Request camera permissions for iOS
        if (iosPwaHandler?.isIOS) {
          await iosPwaHandler.updateManifest({
            additionalFeatures: ['camera'],
            permissions: ['camera']
          });
        }

        // Request camera stream with environmental camera
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        
        // Store the stream reference for later cleanup
        streamRef.current = stream;

        // Update permissions state
        if (isIOS) {
          localStorage.setItem('camera-permission-granted', 'true');
        }
        setHasCameraPermission(true);
        setShowRetryButton(false);
        setMessage(null);

        // Set up video element if it exists
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Create and configure QR scanner
          const scanner = new QrScanner(
            videoRef.current,
            result => handleScan(result.data),
            {
              onDecodeError: () => {
                console.log('QR scan attempt - no code found');
              },
              maxScansPerSecond: 1,
              highlightScanRegion: true,
              highlightCodeOutline: true,
              returnDetailedScanResult: true
            }
          );
          
          // Store scanner reference for later cleanup
          qrScannerRef.current = scanner;
          scanner.start();
        }
      } catch (error) {
        console.error('Camera access error:', error);
        setHasCameraPermission(false);
        setMessage({
          text: isIOS ? 
            'Please grant camera access in your device settings and reload the page' : 
            'Unable to access camera. Please check permissions.',
          type: 'error'
        });
        setShowRetryButton(true);
      }
    };

    initializeScanner();

    // Clean up on unmount or when showScanner becomes false
    return cleanupCameraResources;
  }, [showScanner, cleanupCameraResources, handleScan, isIOS, iosPwaHandler]);

  // Handle scanner opening with permission checks
  const handleScannerOpen = useCallback(async () => {
    try {
      console.log('Opening scanner...');
      
      // Check if we need to handle iOS permissions
      if (iosPwaHandler?.isIOS && !localStorage.getItem('camera-permission-granted')) {
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
          localStorage.setItem('camera-permission-granted', 'true');
          setShowScanner(true);
          setShowRetryButton(false);
          setMessage(null);
        } catch (error) {
          if (error.name === 'NotAllowedError') {
            setMessage({
              text: 'Please grant camera access in iOS Settings and reload the page',
              type: 'error'
            });
            setShowRetryButton(true);
          }
        }
      } else {
        // Non-iOS flow
        await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        setShowScanner(true);
        setShowRetryButton(false);
        setMessage(null);
      }
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setMessage({
          text: 'Camera access is required for scanning QR codes. Allow permissions from settings',
          type: 'error'
        });
        setShowRetryButton(true);
      } else {
        setMessage({
          text: 'Error accessing camera: ' + error.message,
          type: 'error'
        });
      }
    }
  }, [iosPwaHandler]);

  // Auto-dismiss messages after timeout
  useEffect(() => {
    let timer;
    if (message) {
      timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [message]);

  // Initialize iOS PWA handler
  useEffect(() => {
    if (program?.vendor_id) {
      const handler = new IOSPWAHandler(
        { 
          business_name: program.name,
          logo_url: program.logo_url,
          id: program.vendor_id
        },
        window.location.pathname.split('/')[2] // Get subUrl from path
      );
      setIosPwaHandler(handler);
      setIsIOS(handler.isIOS);

      // Update manifest with camera permission and add iOS meta tags
      if (handler.isIOS) {
        handler.addIOSMetaTags();
        handler.updateManifest({
          additionalFeatures: ['camera'],
          permissions: ['camera']
        });
      }
    }
  }, [program]);

  // Helper to format dates consistently
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };

  // Voucher redemption handler
  const handleRedeemVoucher = async (voucherId, pin) => {
    try {
      setIsRedeeming(true);
      await axios.post('/api/customers/redeem-voucher', { 
        voucherId, 
        pin,
        programId: program.id 
      });
      
      fetchProgramData();
      setShowPinModal(false);
      setSelectedVoucher(null);
      alert('Voucher redeemed successfully');
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      alert(error.response?.data?.error || 'Error redeeming voucher');
    } finally {
      setIsRedeeming(false);
    }
  };

  // PIN redemption handler
  const handlePinRedeem = async (pin) => {
    try {
      setIsRedeeming(true);
      await axios.post('/api/customers/redeem-stamp-pin', {
        programId: program.id,
        pin
      });
      
      setShowPinRedeemModal(false);
      setMessage({
        text: 'Visit redeemed successfully',
        type: 'success'
      });
      await fetchProgramData();
    } catch (error) {
      setShowPinRedeemModal(false);
      setMessage({
        text: error.response?.data?.error || 'Error redeeming visit',
        type: 'error'
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Render voucher redemption button based on vendor settings
  const renderVoucherRedemptionButton = (voucher) => {
    if (voucher.redeemed) {
      return null;
    }
    
    if (isLoadingVendorSettings) {
      return <div>Loading...</div>;
    }
    
    if (!vendorSettings?.customerRedemptionEnabled) {
      return null;
    }
  
    return (
      <button
        onClick={() => {
          setSelectedVoucher(voucher.id);
          setShowPinModal(true);
        }}
        className="redeem-voucher-btn"
      >
        Redeem Voucher
      </button>
    );
  };

  if (!program) return <div>Loading...</div>;

  return (
    <div className="loyalty-card-page" style={{ maxWidth: '555px', margin: '0 auto' }}>
      <div className="loyalty-card-header">
        <div className="header-actions">
          <button onClick={() => navigate(-1)} className="back-button">← Back</button>
          {vendorSettings?.customerRedemptionEnabled && (
            <button 
              onClick={() => setShowPinRedeemModal(true)}
              className="pin-redeem-button"
            >
              Redeem with PIN
            </button>
          )}
        </div>
      </div>
      
      {isLoading && (
        <div className="loading-overlay-card">
          <div className="loader-card"></div>
        </div>
      )}

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          {showRetryButton && (
            <button 
              onClick={handleScannerOpen} 
              className="retry-button"
            >
              Try Again
            </button>
          )}
        </div>
      )}

      {showScanner && (
        <div className="qr-scanner-container">
          {!hasCameraPermission && (
            <div className={`camera-fallback ${isIOS ? 'ios' : ''}`}>
              <p>Camera access is required for scanning.</p>
              {isIOS && (
                <p>Please ensure camera permissions are granted in your device settings.</p>
              )}
              {showRetryButton && (
                <button 
                  onClick={handleScannerOpen} 
                  className="retry-button"
                >
                  Try Again
                </button>
              )}
              <button 
                onClick={() => {
                  cleanupCameraResources();
                  setShowScanner(false);
                }} 
                className="close-scanner-btn"
              >
                Close
              </button>
            </div>
          )}
          <video 
            ref={videoRef} 
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            playsInline
            autoPlay
            muted
          />
          {hasCameraPermission && (
            <button 
              onClick={() => {
                cleanupCameraResources();
                setShowScanner(false);
              }} 
              className="close-scanner-btn"
            >
              Close Scanner
            </button>
          )}
        </div>
      )}

      <div className="loyalty-card">
        <h3 className="program-name">{program.name}</h3>
        <div className="stamps-container">
          {[...Array(parseInt(program.stamps_required))].map((_, index) => (
            <div
              key={index}
              className={`stamp ${index < program.stamps ? 'active' : ''} ${index === lastRedeemedStamp ? 'redeemed' : ''}`}
            >
              📍
            </div>
          ))}
        </div>
        <p className="stamp-count">Stamps: {program.stamps}/{program.stamps_required}</p>
        <p className="cards-completed">Cards Completed: {program.cards_completed || 0}</p>
        <button 
          onClick={handleScannerOpen} 
          id="capture-visit-button"
        >
          📸 Capture Visit
        </button>
        <button onClick={() => setShowAbout(!showAbout)} className="about-button">
          {showAbout ? 'Hide Details' : 'Show Details'}
        </button>
        {showAbout && (
          <div className="about-section">
            <p>{program.about || 'No additional information available.'}</p>
          </div>
        )}
        <h4 className="vouchers-title">Vouchers</h4>
        <div className="vouchers-container">
          {program.vouchers && program.vouchers.length > 0 ? (
            program.vouchers.map(voucher => (
              <div key={voucher.id} className={`voucher ${voucher.redeemed ? 'redeemed' : 'available'}`}>
                <div className="voucher-content">
                  <p className="voucher-description">{program.description || 'No description'}</p>
                  <p className="voucher-id">Voucher ID: {voucher.id}</p>
                  <p className="voucher-status">Status: {voucher.redeemed ? 'Redeemed' : 'Available'}</p>
                  {voucher.redeemed && <p className="voucher-redeemed-date">Redeemed at: {formatDate(voucher.redeemed_at)}</p>}
                  <p className="voucher-created-date">Created at: {formatDate(voucher.created_at)}</p>
                  {!voucher.redeemed && renderVoucherRedemptionButton(voucher)}
                </div>
              </div>
            ))
          ) : (
            <p className="no-vouchers">No vouchers available</p>
          )}
        </div>
      </div>
      
      {showPinModal && (
        <PinRedemptionModal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setSelectedVoucher(null);
          }}
          onSubmit={(pin) => handleRedeemVoucher(selectedVoucher, pin)}
          isSubmitting={isRedeeming}
        />
      )}
      
      {showPinRedeemModal && (
        <PinRedemptionModal
          isOpen={showPinRedeemModal}
          onClose={() => setShowPinRedeemModal(false)}
          onSubmit={handlePinRedeem}
          isSubmitting={isRedeeming}
        />
      )}
    </div>
  );
}

export default LoyaltyCard;